// Controller + keyboard input, mapped like Tony Hawk's Pro Skater on a
// PlayStation pad. Browsers expose pads through the Gamepad API's "standard"
// mapping, so an Xbox pad works too (A=X, B=O, X=Square, Y=Triangle).
//
//   Left stick / D-pad   steer on the ground, spin in the air, pick trick direction
//   X  (Cross / A)       hold to crouch, release to ollie
//   □  (Square / X)      flip tricks
//   ○  (Circle / B)      grab tricks (hold to keep grabbing)
//   △  (Triangle / Y)    grind (press near a rail or ledge)
//   L1 / R1              fast spin in the air
//   R2                   revert after landing on a ramp; spine transfer / acid drop in the air; switch stance on flat
//   L2                   nollie stance (the next ollie pops off the nose)
//   Start / Select       pause menu / toggle controls help
//
// The actions on face buttons, shoulders and triggers can be remapped in
// Settings (#36, `settings.ts`); these are the defaults.

import { DEFAULT_KEYS, DEFAULT_PAD, REMAPPABLE, type Action } from "./settings";
import { markPadSeen, padSeenBefore } from "./controller-nudge";
import { storage } from "./storage";
import { TouchPad } from "./touch";

export type Button =
  | "ollie"
  | "flip"
  | "grab"
  | "grind"
  | "spinL"
  | "spinR"
  | "revert"
  | "nollie"
  | "pause"
  | "reset"
  | "help";

export const BUTTONS: Button[] = [
  "ollie",
  "flip",
  "grab",
  "grind",
  "spinL",
  "spinR",
  "revert",
  "nollie",
  "pause",
  "reset",
  "help",
];

export interface RawInput {
  moveX: number;
  /** +1 is stick up / forward. */
  moveY: number;
  held: Record<Button, boolean>;
}

export interface InputFrame extends RawInput {
  pressed: Record<Button, boolean>;
  released: Record<Button, boolean>;
  source: "gamepad" | "keyboard" | "touch";
}

export const DEADZONE = 0.22;

function emptyButtons(): Record<Button, boolean> {
  return Object.fromEntries(BUTTONS.map((b) => [b, false])) as Record<Button, boolean>;
}

export function emptyRaw(): RawInput {
  return { moveX: 0, moveY: 0, held: emptyButtons() };
}

function deadzone(v: number): number {
  if (Math.abs(v) < DEADZONE) return 0;
  return Math.sign(v) * ((Math.abs(v) - DEADZONE) / (1 - DEADZONE));
}

type PadLike = Pick<Gamepad, "axes" | "buttons">;

/** L2 and R2: analog triggers on the standard mapping. */
const TRIGGERS = [6, 7];

/**
 * Reads a standard-mapping gamepad. D-pad and left stick are merged, like THPS on PS1.
 * `prev` is this pad's previous reading, used so analog triggers don't chatter.
 * `map` says which button index each action is on.
 */
export function readGamepad(pad: PadLike, prev?: RawInput, map: Readonly<Record<Action, number>> = DEFAULT_PAD): RawInput {
  const b = (i: number) => {
    const btn = pad.buttons[i];
    return btn ? btn.pressed || btn.value > 0.5 : false;
  };
  // L2 / R2 are analog. A trigger resting near one press threshold flickers
  // on and off, which toggled nollie stance twice in one squeeze. Press past
  // halfway, release below a quarter.
  const trigger = (i: number, wasHeld: boolean) => {
    const v = pad.buttons[i]?.value ?? 0;
    return wasHeld ? v > 0.25 : v > 0.5;
  };
  let x = deadzone(pad.axes[0] ?? 0);
  let y = deadzone(-(pad.axes[1] ?? 0));
  if (b(14)) x = -1;
  if (b(15)) x = 1;
  if (b(12)) y = 1;
  if (b(13)) y = -1;
  const held = emptyButtons();
  for (const a of REMAPPABLE) {
    const i = map[a];
    held[a] = TRIGGERS.includes(i) ? trigger(i, prev?.held[a] ?? false) : b(i);
  }
  held.pause = b(9);
  held.reset = false; // reset lives in the pause menu on a pad
  held.help = b(8);
  return { moveX: x, moveY: y, held };
}

/** Keys that aren't remappable: pause, reset and help. */
const FIXED_KEYS: Record<string, Button> = {
  KeyR: "reset",
  Escape: "pause",
  KeyP: "pause",
  KeyH: "help",
};

/** Reads the keyboard; `map` gives each action's keys. */
export function readKeyboard(keys: ReadonlySet<string>, map: Readonly<Record<Action, readonly string[]>> = DEFAULT_KEYS): RawInput {
  const has = (...codes: string[]) => codes.some((c) => keys.has(c));
  const held = emptyButtons();
  for (const a of REMAPPABLE) held[a] = has(...map[a]);
  for (const [code, button] of Object.entries(FIXED_KEYS)) if (keys.has(code)) held[button] = true;
  return {
    moveX: (has("ArrowRight", "KeyD") ? 1 : 0) - (has("ArrowLeft", "KeyA") ? 1 : 0),
    moveY: (has("ArrowUp", "KeyW") ? 1 : 0) - (has("ArrowDown", "KeyS") ? 1 : 0),
    held,
  };
}

function active(r: RawInput | null | undefined): boolean {
  return r != null && (r.moveX !== 0 || r.moveY !== 0 || BUTTONS.some((k) => r.held[k]));
}

/** Combines sources (any can drive) and derives press/release edges from the previous frame. */
export function buildFrame(
  prev: RawInput,
  pad: RawInput | null,
  keyboard: RawInput,
  touch?: RawInput,
): { frame: InputFrame; raw: RawInput } {
  const raw = emptyRaw();
  const pick = (...v: number[]) => v.reduce((a, b) => (Math.abs(a) >= Math.abs(b) ? a : b));
  raw.moveX = pick(pad?.moveX ?? 0, keyboard.moveX, touch?.moveX ?? 0);
  raw.moveY = pick(pad?.moveY ?? 0, keyboard.moveY, touch?.moveY ?? 0);
  const pressed = emptyButtons();
  const released = emptyButtons();
  for (const k of BUTTONS) {
    raw.held[k] = Boolean(pad?.held[k]) || keyboard.held[k] || Boolean(touch?.held[k]);
    pressed[k] = raw.held[k] && !prev.held[k];
    released[k] = !raw.held[k] && prev.held[k];
  }
  const source = active(pad) ? "gamepad" : active(touch) ? "touch" : "keyboard";
  return { frame: { ...raw, pressed, released, source }, raw };
}

/** A phone or tablet: touch is the only pointer (touch wording, the on-screen controls, Auto quality Low). */
export function touchOnly(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(pointer: coarse)").matches && !window.matchMedia("(any-pointer: fine)").matches;
}

/** Owns the browser listeners. `poll()` once per rendered frame. */
export class InputManager {
  private keys = new Set<string>();
  /** Keys that went down since the last poll, so a tap shorter than a frame still registers. */
  private tapped = new Set<string>();
  private prev = emptyRaw();
  /** When the last poll ran (ms), for the touch stick's smoothing. */
  private lastPoll: number | null = null;
  private prevPad: RawInput | undefined;
  private lastSource: InputFrame["source"] = "keyboard";
  padName: string | null = null;
  /** A pad has connected on this device, now or on an earlier visit (#161: no more controller nudges). */
  padSeen = padSeenBefore(storage());
  /** Current bindings (Settings). */
  padMap: Readonly<Record<Action, number>> = DEFAULT_PAD;
  keyMap: Readonly<Record<Action, readonly string[]>> = DEFAULT_KEYS;
  /** For remapping: the first pad button index and the first key that went down in the last poll. */
  padPress: number | null = null;
  keyPress: string | null = null;
  private padButtons: boolean[] = [];
  private keyOrder: string[] = [];
  /** True when the page is framed without gamepad permission, so only the keyboard works. */
  padBlocked = false;
  /** The on-screen stick and buttons (#67); `ui/TouchControls.tsx` feeds it. */
  touch = new TouchPad();

  private onKeyDown = (e: KeyboardEvent) => {
    if (e.code.startsWith("Arrow") || e.code === "Space") e.preventDefault();
    this.keys.add(e.code);
    this.tapped.add(e.code);
    if (!e.repeat) this.keyOrder.push(e.code);
  };
  private onKeyUp = (e: KeyboardEvent) => this.keys.delete(e.code);
  private onBlur = () => {
    this.keys.clear();
    this.tapped.clear();
    this.touch.clear();
  };

  constructor(private target: Window = window) {
    target.addEventListener("keydown", this.onKeyDown);
    target.addEventListener("keyup", this.onKeyUp);
    target.addEventListener("blur", this.onBlur);
  }

  /** Which source last drove the skater. */
  get source(): InputFrame["source"] {
    return this.lastSource;
  }

  poll(): InputFrame {
    let pads: (Gamepad | null)[] = [];
    try {
      if (typeof navigator !== "undefined" && navigator.getGamepads) pads = navigator.getGamepads();
    } catch {
      // A cross-origin iframe without allow="gamepad" throws SecurityError here.
      this.padBlocked = true;
    }
    const pad = Array.from(pads).find((p): p is Gamepad => Boolean(p && p.connected)) ?? null;
    this.padName = pad ? pad.id : null;
    if (pad && !this.padSeen) {
      this.padSeen = true;
      markPadSeen(storage());
    }
    const keys = new Set([...this.keys, ...this.tapped]);
    this.tapped.clear();
    this.keyPress = this.keyOrder[0] ?? null;
    this.keyOrder = [];
    const buttons = pad ? pad.buttons.map((btn) => btn.pressed || btn.value > 0.5) : [];
    this.padPress = buttons.findIndex((down, i) => down && !this.padButtons[i]);
    if (this.padPress < 0) this.padPress = null;
    this.padButtons = buttons;
    const padRaw = pad ? readGamepad(pad, this.prevPad, this.padMap) : null;
    this.prevPad = padRaw ?? undefined;
    const { frame, raw } = buildFrame(this.prev, padRaw, readKeyboard(keys, this.keyMap), this.touch.read(this.sincePoll()));
    this.prev = raw;
    // Sticky source: only flip to keyboard when a key is actually used.
    if (frame.source !== "keyboard") this.lastSource = frame.source;
    else if (keys.size > 0) this.lastSource = "keyboard";
    return { ...frame, source: this.lastSource };
  }

  /** Seconds since the last poll, capped so a stall doesn't count as smoothing time. */
  private sincePoll(): number {
    const now = typeof performance !== "undefined" ? performance.now() : Date.now();
    const dt = this.lastPoll === null ? 0 : Math.min(0.1, (now - this.lastPoll) / 1000);
    this.lastPoll = now;
    return dt;
  }

  dispose(): void {
    this.target.removeEventListener("keydown", this.onKeyDown);
    this.target.removeEventListener("keyup", this.onKeyUp);
    this.target.removeEventListener("blur", this.onBlur);
  }
}
